import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { SessionData, RoofModel, RoofFace, Obstruction } from '../types';
import { MODULES } from './equipment';
import type { ImageryResult } from './satellite';

// ============================================================
// THREE.JS SCENE MANAGER
// Renders a realistic 3D building with walls, pitched roof,
// solar panels on roof surfaces, and 3D obstructions
// ============================================================

export class SceneManager {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private controls: OrbitControls;
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;
  private container: HTMLElement;

  private roofMeshes: THREE.Mesh[] = [];
  private panelMeshes: THREE.Mesh[] = [];
  private obstructionMeshes: THREE.Mesh[] = [];
  private shadeOverlayMeshes: THREE.Mesh[] = [];
  private edgeLines: THREE.Line[] = [];
  private wallMeshes: THREE.Mesh[] = [];

  private shadeVisible = true;
  private onPanelClick?: (panelId: string) => void;
  private onPanelHover?: (info: string, x: number, y: number) => void;
  private onHoverEnd?: () => void;
  private removeMode = false;
  private imageryMesh: THREE.Mesh | null = null;
  private gridHelper: THREE.GridHelper | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0e17);
    this.scene.fog = new THREE.Fog(0x0a0e17, 150, 600);

    this.camera = new THREE.PerspectiveCamera(50, container.clientWidth / container.clientHeight, 0.1, 2000);
    this.camera.position.set(60, 50, 60);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI / 2.05;
    this.controls.minDistance = 10;
    this.controls.maxDistance = 400;

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    this.setupLighting();
    this.setupGround();
    this.setupEventListeners();
    this.animate();
  }

  // ============================================================
  // SETUP
  // ============================================================

  private setupLighting() {
    const ambient = new THREE.AmbientLight(0x506070, 0.5);
    this.scene.add(ambient);

    const sun = new THREE.DirectionalLight(0xfff5e0, 1.2);
    sun.position.set(60, 100, 40);
    sun.castShadow = true;
    sun.shadow.mapSize.width = 2048;
    sun.shadow.mapSize.height = 2048;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 400;
    sun.shadow.camera.left = -120;
    sun.shadow.camera.right = 120;
    sun.shadow.camera.top = 120;
    sun.shadow.camera.bottom = -120;
    sun.shadow.bias = -0.0005;
    this.scene.add(sun);

    const hemi = new THREE.HemisphereLight(0x87ceeb, 0x1a1a2e, 0.35);
    this.scene.add(hemi);
  }

  private setupGround() {
    const groundGeo = new THREE.PlaneGeometry(600, 600);
    const groundMat = new THREE.MeshStandardMaterial({ color: 0x152015, roughness: 0.95 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.02;
    ground.receiveShadow = true;
    this.scene.add(ground);

    this.gridHelper = new THREE.GridHelper(200, 40, 0x1e293b, 0x1e293b);
    this.scene.add(this.gridHelper);
  }

  // ============================================================
  // SATELLITE IMAGERY
  // ============================================================

  async loadSatelliteImagery(imagery: ImageryResult) {
    if (this.imageryMesh) {
      this.scene.remove(this.imageryMesh);
      this.imageryMesh.geometry.dispose();
      (this.imageryMesh.material as THREE.Material).dispose();
      this.imageryMesh = null;
    }
    if (this.gridHelper) this.gridHelper.visible = false;

    const texture = await new Promise<THREE.Texture>((resolve) => {
      new THREE.TextureLoader().load(imagery.dataUrl, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        resolve(tex);
      });
    });

    const planeGeo = new THREE.PlaneGeometry(imagery.widthFeet, imagery.heightFeet);
    const planeMat = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.85, metalness: 0 });
    const plane = new THREE.Mesh(planeGeo, planeMat);
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = 0;
    plane.receiveShadow = true;
    plane.position.x = imagery.centerOffsetX - imagery.widthFeet / 2;
    plane.position.z = imagery.centerOffsetY - imagery.heightFeet / 2;
    this.scene.add(plane);
    this.imageryMesh = plane;
  }

  clearSatelliteImagery() {
    if (this.imageryMesh) {
      this.scene.remove(this.imageryMesh);
      this.imageryMesh.geometry.dispose();
      (this.imageryMesh.material as THREE.Material).dispose();
      this.imageryMesh = null;
    }
    if (this.gridHelper) this.gridHelper.visible = true;
  }

  // ============================================================
  // EVENT HANDLERS
  // ============================================================

  private setupEventListeners() {
    this.renderer.domElement.addEventListener('click', this.onCanvasClick);
    this.renderer.domElement.addEventListener('mousemove', this.onCanvasMouseMove);
    window.addEventListener('resize', this.onResize);
  }

  private onResize = () => {
    this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
  };

  private onCanvasClick = (event: MouseEvent) => {
    if (!this.removeMode || this.panelMeshes.length === 0) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.panelMeshes);
    if (intersects.length > 0) {
      const panel = intersects[0].object as THREE.Mesh;
      this.scene.remove(panel);
      panel.geometry.dispose();
      (panel.material as THREE.Material).dispose();
      this.panelMeshes = this.panelMeshes.filter(p => p !== panel);
      this.onPanelClick?.(panel.userData.panelId);
    }
  };

  private onCanvasMouseMove = (event: MouseEvent) => {
    if (this.panelMeshes.length === 0 || this.removeMode) {
      this.onHoverEnd?.();
      return;
    }
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.panelMeshes);
    if (intersects.length > 0) {
      const panel = intersects[0].object as THREE.Mesh;
      this.onPanelHover?.(panel.userData.tooltip, event.clientX - rect.left, event.clientY - rect.top);
    } else {
      this.onHoverEnd?.();
    }
  };

  private animate = () => {
    requestAnimationFrame(this.animate);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  // ============================================================
  // PUBLIC API
  // ============================================================

  setRemoveMode(enabled: boolean) {
    this.removeMode = enabled;
    this.renderer.domElement.style.cursor = enabled ? 'crosshair' : 'grab';
  }

  setShadeVisible(visible: boolean) {
    this.shadeVisible = visible;
    this.shadeOverlayMeshes.forEach(m => (m.visible = visible));
  }

  /** Render one frame then capture a downscaled JPEG data URL.
   *  Render-then-capture is required because the WebGL buffer is
   *  not preserved between frames (preserveDrawingBuffer: false). */
  captureSnapshot(maxWidth = 320): string | null {
    this.renderer.render(this.scene, this.camera);
    const src = this.renderer.domElement;
    if (src.width === 0 || src.height === 0) return null;
    const scale = Math.min(1, maxWidth / src.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(src.width * scale);
    canvas.height = Math.round(src.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.7);
  }

  setCallbacks(callbacks: {
    onPanelClick?: (panelId: string) => void;
    onPanelHover?: (info: string, x: number, y: number) => void;
    onHoverEnd?: () => void;
  }) {
    this.onPanelClick = callbacks.onPanelClick;
    this.onPanelHover = callbacks.onPanelHover;
    this.onHoverEnd = callbacks.onHoverEnd;
  }

  clearScene() {
    [...this.roofMeshes, ...this.panelMeshes, ...this.obstructionMeshes, ...this.shadeOverlayMeshes, ...this.wallMeshes].forEach(m => {
      this.scene.remove(m);
      m.geometry?.dispose();
      (m.material as THREE.Material)?.dispose();
    });
    this.edgeLines.forEach(l => {
      this.scene.remove(l);
      l.geometry?.dispose();
      (l.material as THREE.Material)?.dispose();
    });
    this.roofMeshes = [];
    this.panelMeshes = [];
    this.obstructionMeshes = [];
    this.shadeOverlayMeshes = [];
    this.wallMeshes = [];
    this.edgeLines = [];
    this.clearSatelliteImagery();
  }

  clearPanels() {
    this.panelMeshes.forEach(m => {
      this.scene.remove(m);
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    });
    this.panelMeshes = [];
  }

  renderDesign(data: SessionData, moduleId: string) {
    this.clearScene();
    const roofModel = data.roof_model;

    // 1. Build walls from the union of all roof faces
    this.renderWalls(roofModel);

    // 2. Render each roof face as a pitched surface
    roofModel.faces.forEach(face => this.renderRoofFace(face));

    // 3. Render obstructions in 3D
    roofModel.obstructions.forEach(obs => this.renderObstruction(obs, roofModel));

    // 4. Render panels on roof surfaces
    data.panel_layout.panels.forEach(panel => this.renderPanel(panel, roofModel, moduleId));

    // 5. Render shade overlay
    this.renderShadeOverlay(roofModel);

    // 6. Frame camera to fit
    this.frameCamera();
  }

  renderPanelsOnly(data: SessionData, moduleId: string) {
    this.clearPanels();
    data.panel_layout.panels.forEach(panel => this.renderPanel(panel, data.roof_model, moduleId));
  }

  // ============================================================
  // 3D RENDERING — WALLS
  // ============================================================

  /**
   * Extrude building walls from the union bounding polygon of all roof faces.
   * For simplicity, we use the overall bounding box of all face vertices.
   */
  private renderWalls(roofModel: RoofModel) {
    const allPoints = roofModel.faces.flatMap(f => f.vertices);
    if (allPoints.length === 0) return;

    const xs = allPoints.map(p => p[0]);
    const ys = allPoints.map(p => p[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    // Wall height is the minimum roof face height
    const wallHeight = Math.min(...roofModel.faces.map(f => f.height));

    // Create wall geometry as a simple box (no interior)
    const wallThickness = 0.5;
    const wallMat = new THREE.MeshStandardMaterial({
      color: 0x8b7355,
      roughness: 0.85,
      side: THREE.DoubleSide,
    });

    // North wall
    const northGeo = new THREE.BoxGeometry(maxX - minX + wallThickness, wallHeight, wallThickness);
    const northWall = new THREE.Mesh(northGeo, wallMat);
    northWall.position.set((minX + maxX) / 2, wallHeight / 2, minY);
    northWall.castShadow = true;
    northWall.receiveShadow = true;
    this.scene.add(northWall);
    this.wallMeshes.push(northWall);

    // South wall
    const southWall = new THREE.Mesh(northGeo.clone(), wallMat.clone());
    southWall.position.set((minX + maxX) / 2, wallHeight / 2, maxY);
    southWall.castShadow = true;
    southWall.receiveShadow = true;
    this.scene.add(southWall);
    this.wallMeshes.push(southWall);

    // East wall
    const eastGeo = new THREE.BoxGeometry(wallThickness, wallHeight, maxY - minY + wallThickness);
    const eastWall = new THREE.Mesh(eastGeo, wallMat);
    eastWall.position.set(maxX, wallHeight / 2, (minY + maxY) / 2);
    eastWall.castShadow = true;
    eastWall.receiveShadow = true;
    this.scene.add(eastWall);
    this.wallMeshes.push(eastWall);

    // West wall
    const westWall = new THREE.Mesh(eastGeo.clone(), wallMat.clone());
    westWall.position.set(minX, wallHeight / 2, (minY + maxY) / 2);
    westWall.castShadow = true;
    westWall.receiveShadow = true;
    this.scene.add(westWall);
    this.wallMeshes.push(westWall);
  }

  // ============================================================
  // 3D RENDERING — ROOF FACES (PITCHED SURFACES)
  // ============================================================

  /**
   * Render a roof face as a pitched surface.
   * The face is a 2D polygon that gets tilted according to its pitch and azimuth.
   * We compute the 3D coordinates of each vertex by applying the pitch rotation.
   */
  private renderRoofFace(face: RoofFace) {
    const pitchRad = face.pitch * Math.PI / 180;
    const azRad = face.azimuth * Math.PI / 180;

    // Compute centroid
    const cx = face.vertices.reduce((s, v) => s + v[0], 0) / face.vertices.length;
    const cy = face.vertices.reduce((s, v) => s + v[1], 0) / face.vertices.length;

    // The pitch direction: the roof surface rises toward the azimuth direction.
    // Azimuth 180 (south) → surface rises toward south (negative Y in our coord system)
    // We compute the height of each vertex based on its distance from the low edge.
    //
    // For a south-facing roof (az=180):
    //   The north edge (higher Y) is the top of the slope (ridge)
    //   The south edge (lower Y) is the bottom (eave)
    //   Height increases as Y increases (toward north/ridge)
    //
    // General formula: height_offset = distance_from_low_edge * tan(pitch)

    // Direction of slope (from low to high), in the XY plane
    // Azimuth is clockwise from north (Y+). So:
    //   az=180 (south): slope direction = (0, -1) → but we want the "up" direction = (0, +1) toward north
    //   Actually: the roof face tilts UP toward the azimuth direction.
    //   For south-facing (az=180), the high edge is on the north side.
    //   So the "up" direction (toward higher points) is opposite to azimuth.
    //   upDir = (-sin(az), -cos(az)) for azimuth measured clockwise from north
    //   Wait, let's think again:
    //   az=180 → the face points south → the high edge is north, low edge is south
    //   So the slope goes UP as we move NORTH (positive Y)
    //   upDir for az=180: (0, +1) → which is -sin(180), -cos(180) = (0, 1) ✓
    //   az=90 (east): high edge is west, low edge is east
    //   upDir = (-sin(90), -cos(90)) = (-1, 0) → slope goes UP as we move WEST ✓

    const upX = -Math.sin(azRad);
    const upY = -Math.cos(azRad);

    // Project each vertex onto the "up" direction to get its relative height
    // Vertex height = baseHeight + (projection_on_up - min_projection) * tan(pitch)
    const projections = face.vertices.map(v => (v[0] - cx) * upX + (v[1] - cy) * upY);
    const minProj = Math.min(...projections);
    const maxProj = Math.max(...projections);

    // Build 3D vertices for the roof surface
    const vertices3D: number[] = [];
    face.vertices.forEach((v, i) => {
      const proj = projections[i];
      const heightOffset = (proj - minProj) * Math.tan(pitchRad);
      const y = face.height + heightOffset * 0.5; // Scale down for visual clarity
      vertices3D.push(v[0], y, v[1]);
    });

    // Build triangulated geometry
    const shape = new THREE.Shape();
    face.vertices.forEach((v, i) => {
      if (i === 0) shape.moveTo(v[0], v[1]);
      else shape.lineTo(v[0], v[1]);
    });
    shape.closePath();

    // Use ShapeGeometry for the flat shape, then we'll manually set vertex heights
    const geo = new THREE.ShapeGeometry(shape);

    // Override the Z coordinates with our computed heights
    const positions = geo.attributes.position;
    for (let i = 0; i < positions.count; i++) {
      const x2 = positions.getX(i);
      const y2 = positions.getY(i);
      // Find the closest vertex to determine height
      // (ShapeGeometry preserves the outline vertices, interior points are triangulated)
      // For simplicity, compute height based on position relative to centroid
      const proj = (x2 - cx) * upX + (y2 - cy) * upY;
      const heightOffset = (proj - minProj) * Math.tan(pitchRad);
      positions.setZ(i, heightOffset * 0.5);
    }
    positions.needsUpdate = true;
    geo.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({
      color: 0x5a4a3a,
      side: THREE.DoubleSide,
      roughness: 0.8,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = face.height * 0.8;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = { type: 'roof', faceId: face.id };
    this.scene.add(mesh);
    this.roofMeshes.push(mesh);

    // Roof edge lines (using the 3D vertices)
    const linePoints: number[] = [];
    for (let i = 0; i < face.vertices.length; i++) {
      const v = face.vertices[i];
      const proj = projections[i];
      const heightOffset = (proj - minProj) * Math.tan(pitchRad);
      linePoints.push(v[0], face.height * 0.8 + heightOffset * 0.5, v[1]);
    }
    // Close the loop
    linePoints.push(linePoints[0], linePoints[1], linePoints[2]);

    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePoints, 3));
    const lineMat = new THREE.LineBasicMaterial({ color: 0x8b8070, linewidth: 2 });
    const lines = new THREE.LineLoop(lineGeo, lineMat);
    this.scene.add(lines);
    this.edgeLines.push(lines);
  }

  // ============================================================
  // 3D RENDERING — OBSTRUCTIONS
  // ============================================================

  private renderObstruction(obs: Obstruction, roofModel: RoofModel) {
    if (obs.type === 'tree') {
      // Trunk
      const trunkGeo = new THREE.CylinderGeometry(0.4, 0.7, obs.height * 0.4, 8);
      const trunkMat = new THREE.MeshStandardMaterial({ color: 0x4a3320, roughness: 0.9 });
      const trunk = new THREE.Mesh(trunkGeo, trunkMat);
      trunk.position.set(obs.position[0], obs.height * 0.2, obs.position[1]);
      trunk.castShadow = true;
      this.scene.add(trunk);

      // Crown — multiple spheres for a more natural look
      const crownMat = new THREE.MeshStandardMaterial({ color: 0x2d5016, roughness: 0.9 });
      const crownY = obs.height * 0.65;
      const crownRadius = obs.width / 2;

      // Main crown
      const crown1 = new THREE.Mesh(new THREE.SphereGeometry(crownRadius, 16, 12), crownMat);
      crown1.position.set(obs.position[0], crownY, obs.position[1]);
      crown1.castShadow = true;
      crown1.scale.set(1, 0.85, 1);
      this.scene.add(crown1);
      this.obstructionMeshes.push(crown1);

      // Small offset spheres for fuller appearance
      if (obs.width > 6) {
        const crown2 = new THREE.Mesh(new THREE.SphereGeometry(crownRadius * 0.7, 12, 10), crownMat);
        crown2.position.set(obs.position[0] + crownRadius * 0.4, crownY - crownRadius * 0.2, obs.position[1]);
        crown2.castShadow = true;
        this.scene.add(crown2);
        this.obstructionMeshes.push(crown2);

        const crown3 = new THREE.Mesh(new THREE.SphereGeometry(crownRadius * 0.6, 12, 10), crownMat);
        crown3.position.set(obs.position[0] - crownRadius * 0.3, crownY + crownRadius * 0.15, obs.position[1] + crownRadius * 0.2);
        crown3.castShadow = true;
        this.scene.add(crown3);
        this.obstructionMeshes.push(crown3);
      }
    } else if (obs.type === 'chimney') {
      const geo = new THREE.BoxGeometry(obs.width, obs.height, obs.width);
      const mat = new THREE.MeshStandardMaterial({ color: 0x8b6b52, roughness: 0.8 });
      const mesh = new THREE.Mesh(geo, mat);
      // Position chimney on the roof surface at the traced location
      mesh.position.set(obs.position[0], roofModel.roofHeight * 0.8 + obs.height / 2, obs.position[1]);
      mesh.castShadow = true;
      this.scene.add(mesh);
      this.obstructionMeshes.push(mesh);
    } else {
      // Vent or other — small cylinder
      const geo = new THREE.CylinderGeometry(obs.width / 2, obs.width / 2, obs.height, 8);
      const mat = new THREE.MeshStandardMaterial({ color: 0x6b7280, roughness: 0.7, metalness: 0.3 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(obs.position[0], roofModel.roofHeight * 0.8 + obs.height / 2, obs.position[1]);
      mesh.castShadow = true;
      this.scene.add(mesh);
      this.obstructionMeshes.push(mesh);
    }
  }

  // ============================================================
  // 3D RENDERING — PANELS
  // ============================================================

  private renderPanel(
    panel: { id: string; face_id: string; position: [number, number, number]; orientation: string },
    roofModel: RoofModel,
    moduleId: string
  ) {
    const mod = MODULES[moduleId];
    const pL = mod.length * 3.28 * 0.95;
    const pW = mod.width * 3.28 * 0.95;

    const geo = new THREE.BoxGeometry(pW, 0.2, pL);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x1a2a6c,
      metalness: 0.7,
      roughness: 0.25,
      emissive: 0x1e40af,
      emissiveIntensity: 0.08,
    });
    const mesh = new THREE.Mesh(geo, mat);

    const face = roofModel.faces.find(f => f.id === panel.face_id);
    if (!face) return;

    const pitchRad = face.pitch * Math.PI / 180;
    const azRad = face.azimuth * Math.PI / 180;

    // Compute the panel's 3D position on the pitched roof surface
    // Same height computation as the roof face
    const cx = face.vertices.reduce((s, v) => s + v[0], 0) / face.vertices.length;
    const cy = face.vertices.reduce((s, v) => s + v[1], 0) / face.vertices.length;
    const upX = -Math.sin(azRad);
    const upY = -Math.cos(azRad);
    const projections = face.vertices.map(v => (v[0] - cx) * upX + (v[1] - cy) * upY);
    const minProj = Math.min(...projections);

    const px = panel.position[0];
    const py = panel.position[1];
    const proj = (px - cx) * upX + (py - cy) * upY;
    const heightOffset = (proj - minProj) * Math.tan(pitchRad);
    const surfaceY = face.height * 0.8 + heightOffset * 0.5 + 0.15;

    mesh.position.set(px, surfaceY, py);

    // Orient panel to match roof surface pitch and azimuth
    mesh.rotation.set(0, 0, 0);
    const tiltAxis = new THREE.Vector3(Math.sin(azRad), 0, -Math.cos(azRad));
    mesh.rotateOnAxis(tiltAxis, pitchRad);

    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = {
      type: 'panel',
      panelId: panel.id,
      orientation: panel.orientation,
      tooltip: `${mod.manufacturer} ${mod.model}\n${mod.wattage}W • ${mod.efficiency}% efficient\nOrientation: ${panel.orientation}`,
    };
    this.scene.add(mesh);
    this.panelMeshes.push(mesh);
  }

  // ============================================================
  // 3D RENDERING — SHADE OVERLAY
  // ============================================================

  private renderShadeOverlay(roofModel: RoofModel) {
    roofModel.faces.forEach(face => {
      if (face.shade_points && face.shade_points.length > 0) {
        // Per-point shade heatmap
        this.renderShadeHeatmap(face);
      } else {
        // Per-face color overlay (simplified)
        this.renderFaceShadeOverlay(face);
      }
    });
  }

  /**
   * Render a per-point shade heatmap using small colored circles
   * for each shade sample point.
   */
  private renderShadeHeatmap(face: RoofFace) {
    if (!face.shade_points) return;

    const pitchRad = face.pitch * Math.PI / 180;
    const azRad = face.azimuth * Math.PI / 180;
    const cx = face.vertices.reduce((s, v) => s + v[0], 0) / face.vertices.length;
    const cy = face.vertices.reduce((s, v) => s + v[1], 0) / face.vertices.length;
    const upX = -Math.sin(azRad);
    const upY = -Math.cos(azRad);
    const projections = face.vertices.map(v => (v[0] - cx) * upX + (v[1] - cy) * upY);
    const minProj = Math.min(...projections);

    face.shade_points.forEach(sp => {
      const color = this.shadeToColor(sp.solar_access);
      const geo = new THREE.CircleGeometry(2, 8); // 2ft radius circles
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.35,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(geo, mat);

      // Compute height on the pitched surface
      const proj = (sp.x - cx) * upX + (sp.y - cy) * upY;
      const heightOffset = (proj - minProj) * Math.tan(pitchRad);
      const surfaceY = face.height * 0.8 + heightOffset * 0.5 + 0.08;

      mesh.position.set(sp.x, surfaceY, sp.y);
      mesh.rotation.x = -Math.PI / 2;

      // Apply pitch rotation
      const tiltAxis = new THREE.Vector3(Math.sin(azRad), 0, -Math.cos(azRad));
      mesh.rotateOnAxis(tiltAxis, pitchRad);

      mesh.userData = { type: 'shade', faceId: face.id };
      mesh.visible = this.shadeVisible;
      this.scene.add(mesh);
      this.shadeOverlayMeshes.push(mesh);
    });
  }

  private renderFaceShadeOverlay(face: RoofFace) {
    const access = face.solar_access;
    const color = this.shadeToColor(access);

    const shape = new THREE.Shape();
    face.vertices.forEach((v, i) => {
      if (i === 0) shape.moveTo(v[0], v[1]);
      else shape.lineTo(v[0], v[1]);
    });
    shape.closePath();

    const geo = new THREE.ShapeGeometry(shape);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.15,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;

    const cx = face.vertices.reduce((s, v) => s + v[0], 0) / face.vertices.length;
    const cy = face.vertices.reduce((s, v) => s + v[1], 0) / face.vertices.length;

    const pitchRad = face.pitch * Math.PI / 180;
    const azRad = face.azimuth * Math.PI / 180;
    const upX = -Math.sin(azRad);
    const upY = -Math.cos(azRad);
    const projections = face.vertices.map(v => (v[0] - cx) * upX + (v[1] - cy) * upY);
    const minProj = Math.min(...projections);
    const proj = (cx - cx) * upX + (cy - cy) * upY; // centroid = 0
    const heightOffset = (proj - minProj) * Math.tan(pitchRad);

    mesh.position.set(cx, face.height * 0.8 + heightOffset * 0.5 + 0.06, cy);

    const tiltAxis = new THREE.Vector3(Math.sin(azRad), 0, -Math.cos(azRad));
    mesh.rotateOnAxis(tiltAxis, pitchRad);

    mesh.userData = { type: 'shade', faceId: face.id };
    mesh.visible = this.shadeVisible;
    this.scene.add(mesh);
    this.shadeOverlayMeshes.push(mesh);
  }

  private shadeToColor(access: number): number {
    if (access >= 80) return 0x22c55e;
    if (access >= 65) return 0xeab308;
    if (access >= 50) return 0xf97316;
    return 0xef4444;
  }

  // ============================================================
  // CAMERA FRAMING
  // ============================================================

  private frameCamera() {
    const box = new THREE.Box3();
    this.scene.traverse(obj => {
      if ((obj as THREE.Mesh).isMesh && obj !== this.imageryMesh && obj !== this.gridHelper) {
        box.expandByObject(obj);
      }
    });

    if (box.isEmpty()) return;

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    this.camera.position.set(
      center.x + maxDim * 0.9,
      maxDim * 0.8,
      center.z + maxDim * 0.9
    );
    this.controls.target.copy(center);
    this.controls.update();
  }

  // ============================================================
  // CLEANUP
  // ============================================================

  dispose() {
    this.clearScene();
    this.renderer.domElement.removeEventListener('click', this.onCanvasClick);
    this.renderer.domElement.removeEventListener('mousemove', this.onCanvasMouseMove);
    window.removeEventListener('resize', this.onResize);
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
